import { CardIssuingBodyData, ICsvValidationResponse } from '@Shared/interfaces';
import {
  AbacusIssuingDetailsInclude,
  CardDetailShipping,
  CardsGatewayJsonImageResponse,
  LegacyAbacusCardIssuingJsonResponse,
  MerchantCategoryCodeGroup,
  ShippingOption,
  Vendor
} from '@Shared/interfaces/CardsGatewayInterfaces';
import { ICardRequestModel, ICardTransactionModel, ILinkedAccountModel } from '@Shared/models';

import { Api as apiService } from '../api';

export interface IParentCardOption extends Pick<ILinkedAccountModel, 'id' | 'name'> {
  last4: string;
  displayName?: string;
}

export interface IGetIssuingDetailsResponse extends LegacyAbacusCardIssuingJsonResponse {
  parent_card?: ILinkedAccountModel;
  card_program?: ILinkedAccountModel;
  user_id?: string; // Populated by front end right now
  shipping?: CardDetailShipping;
}

export interface IGetParentCardsResponse {
  results: IParentCardOption[];
}

export interface IGetTransactionOptions extends Record<string, string> {
  relateOcrReceipts: string;
}

export interface CardIssuingData {
  budget: string;
  cardName: string;
  city: string;
  email: string;
  frequency: string;
  recipient: string;
  state: string;
  street: string;
  zipCode: string;
  transactionLimit?: string;
}

export interface BulkPhysicalCardRequestData {
  allowance_amount: number;
  allowance_interval: string;
  assignee_email: string;
  parent_linked_account_id: string;
  card_name: string;
  address_recipient: string;
  address_street: string;
  address_city: string;
  address_state: string;
  address_zip_code: string;
  allowance_transaction_limit?: number;
}

export interface ICardValidationResponse extends ICsvValidationResponse<CardIssuingData> {
  existingPhysicalCardsEmails: string[];
}

export abstract class CardIssuingApiService {
  static post(body: CardIssuingBodyData) {
    // TODO front-end error handling? Currently all exists on back-end, but we could preemptively check fields before allowing request to go through at all.
    return apiService
      .post('/api/web/accounts/cards/issue_card', body)
      .then((response) => response.json() as Promise<ILinkedAccountModel | ICardRequestModel>);
  }

  /**
   * Gets issuing details (card information, on the provider end) for a linked account.
   * @param linkedAccountId
   * @param includeShareableLink - denotes whether or not, in the response, to return a url to render a visual representation of the card
   * @returns {Object} - payload of request to provider for card issuing details.
   */
  static getIssuingDetails(
    linkedAccountId: string,
    includeSharedLink?: boolean,
    includeExpenseTags?: boolean
  ): Promise<IGetIssuingDetailsResponse> {
    const options = [];
    if (includeSharedLink) {
      options.push(['include', AbacusIssuingDetailsInclude.shared_link]);
    }

    if (includeExpenseTags) {
      options.push(['include', AbacusIssuingDetailsInclude.expense_tags]);
    }

    const urlParamsString = new URLSearchParams(options).toString();
    const url = `/api/web/accounts/cards/${linkedAccountId}/issuing_details${urlParamsString ? `?${urlParamsString}` : ''}`;

    return apiService.responseToJSONAndToastError(apiService.get(url));
  }

  static updateIssuingDetails(linkedAccountId: string, body: any) {
    // TODO front-end error handling? Currently all exists on back-end, but we could preemptively check fields before allowing request to go through at all.
    return apiService
      .put(`/api/web/accounts/cards/${linkedAccountId}/issuing_details`, body)
      .then((response) => response.json() as Promise<any>);
  }

  static getParentCards(programId: string): Promise<IGetParentCardsResponse> {
    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/accounts/cards/${programId}/parent_cards`));
  }

  static getMerchantCategoryOptions(): Promise<MerchantCategoryCodeGroup[]> {
    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/accounts/cards/merchant_category_options`));
  }

  static getVendors(): Promise<Vendor[]> {
    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/accounts/cards/vendors`));
  }

  static getShippingOptions(programId: string): Promise<ShippingOption[]> {
    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/accounts/cards/${programId}/shipping_options`));
  }

  static getTransactions(linkedAccountId: string, state: string, options?: IGetTransactionOptions): Promise<ICardTransactionModel[]> {
    const searchParams = options ? new URLSearchParams(options).toString() : '';
    const url = `/api/web/accounts/cards/${linkedAccountId}/transactions/${state}?${searchParams}`;

    return apiService.responseToJSONAndToastError(apiService.get(url));
  }

  static searchTransactions(
    linkedAccountId: string,
    limit: number,
    offset: number,
    state?: string,
    sortField?: string,
    sortDirection?: 'asc' | 'desc'
  ): Promise<{ count: number; rows: ICardTransactionModel[] }> {
    const options: Record<string, string> = { limit: limit?.toString(), offset: offset?.toString() };

    if (state) {
      options.state = state;
    }

    if (sortField) {
      options.sortField = sortField;
      options.sortDirection = sortDirection;
    }

    const searchParams = new URLSearchParams(options).toString();

    return apiService.responseToJSONAndToastError(apiService.get(`api/web/accounts/cards/${linkedAccountId}/transactions?${searchParams}`));
  }

  // The URL for this request is returned from getIssuingDetails when includeSharedLink=true and the card is a gateway virtual card
  static getJsonCardImage(src: string): Promise<CardsGatewayJsonImageResponse> {
    return apiService.responseToJSON(fetch(src, { mode: 'cors' }));
  }

  static sendReminder(userId: string, message: string) {
    const url = `/api/web/accounts/cards/${userId}/remind`;

    return apiService.post(url, { message });
  }

  static sendBulkReminder(userIds: string[], message: string) {
    const url = '/api/web/accounts/cards/bulk_remind';
    return apiService.post(url, { message, user_ids: userIds });
  }

  static uploadCSV(data: string, programId: string): Promise<ICardValidationResponse> {
    const url = '/api/web/accounts/cards/upload_csv';

    return apiService.post(url, { csv: data, accountId: programId }).then((response: Response) => response.json());
  }
  static bulkIssueCards(cardsData: BulkPhysicalCardRequestData[], programId: string): Promise<ICardValidationResponse> {
    const url = '/api/web/accounts/cards/bulk_issue_cards';

    return apiService.post(url, { cardsData, parentLinkedAccountId: programId }).then((response: Response) => response.json());
  }
}
